import message from "@/utils/globalMessage";
import React, { useState, useRef, useEffect } from 'react';
import ProTable from "@components/ResponsiveProTable";
import { ModalForm, ProFormText, ProFormSelect } from "@ant-design/pro-form";
import { Button, ConfigProvider, Popconfirm, Space, Input, Form } from "antd";
import enUSIntl from "antd/lib/locale/en_US";
import { useDispatch } from "react-redux";
import { api } from "@redux/api";
import actionsCategory from "@redux/facilityCategory/actions";

import moment from 'moment';
import { useAuth, AUTH_LEVEL } from '@/hooks/use-auth';
import { useSmartNavigate } from '@/hooks/use-smart-navigate';

const Facility_Category = () => {
    const actionRef = useRef();
    const dispatch = useDispatch();
    const [flatData, setFlatData] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [form] = Form.useForm();
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const canWrite = useAuth('FacilityCategory', AUTH_LEVEL.WRITE);
    const canFullAccess = useAuth('FacilityCategory', AUTH_LEVEL.FULL_ACCESS);
    const { navigateAndCloseTabs } = useSmartNavigate();

    // 获取所有分类选项（用于 parentId 选择器）
    const getAllCategoryOptions = () => {
        return flatData
            .filter(item => !editingRecord || item.id !== editingRecord.id)
            .map(item => ({
                label: item.name,
                value: item.id,
            }));
    };

    // 获取所有行的 key（包括有子节点和没有子节点的，用于默认展开状态）
    const getAllRowKeys = (treeData) => {
        const keys = [];
        const traverse = (nodes) => {
            nodes.forEach(node => {
                keys.push(node.id);
                if (node.children && node.children.length > 0) {
                    traverse(node.children);
                }
            });
        };
        traverse(treeData);
        return keys;
    };

    // 将平铺数据转换为树形结构
    const convertToTreeData = (flatData) => {
        if (!Array.isArray(flatData) || flatData.length === 0) {
            return [];
        }

        // 创建映射表，key为id，value为节点对象
        const nodeMap = new Map();
        const rootNodes = [];

        // 第一遍遍历：创建所有节点并建立映射
        flatData.forEach(item => {
            const node = {
                ...item,
                children: []
            };
            nodeMap.set(item.id, node);
        });

        // 第二遍遍历：建立父子关系
        flatData.forEach(item => {
            const node = nodeMap.get(item.id);
            if (item.parentId && nodeMap.has(item.parentId)) {
                // 有父节点，添加到父节点的children中
                const parentNode = nodeMap.get(item.parentId);
                parentNode.children.push(node);
            } else {
                // 没有父节点或父节点不存在，作为根节点
                rootNodes.push(node);
            }
        });

        return rootNodes;
    };

    // 处理创建
    const handleCreate = async (values) => {
        try {
            await api.createFacilityCategory({
                name: values.name,
                parentId: values.parentId || null,
                color: values.color || '#000000',
            });
            message.success('Category created successfully');
            setModalVisible(false);
            form.resetFields();
            if (actionRef.current) {
                actionRef.current.reload();
            }
            return true;
        } catch (error) {
            message.error('Failed to create category');
            return false;
        }
    };

    // 处理编辑
    const handleEdit = async (values) => {
        try {
            await api.editFacilityCategory(editingRecord.id, {
                name: values.name,
                parentId: values.parentId || null,
                color: values.color || '#000000',
            });
            message.success('Category updated successfully');
            setModalVisible(false);
            setEditingRecord(null);
            form.resetFields();
            if (actionRef.current) {
                actionRef.current.reload();
            }
            return true;
        } catch (error) {
            message.error('Failed to update category');
            return false;
        }
    };

    // 处理删除
    const handleDelete = async (id) => {
        try {
            await api.deleteFacilityCategory(id);
            message.success('Category deleted successfully');
            if (actionRef.current) {
                actionRef.current.reload();
            }
        } catch (error) {
            message.error('Failed to delete category');
        }
    };

    // 打开编辑弹窗
    const handleEditClick = (record) => {
        setEditingRecord(record);
        form.setFieldsValue({
            name: record.name,
            parentId: record.parentId || undefined,
            color: record.color || '#000000',
        });
        setModalVisible(true);
    };

    // 打开创建弹窗
    const handleCreateClick = () => {
        setEditingRecord(null);
        form.resetFields();
        form.setFieldsValue({
            color: '#000000',
        });
        setModalVisible(true);
    };

    // 关闭弹窗
    const handleModalClose = () => {
        setModalVisible(false);
        setEditingRecord(null);
        form.resetFields();
    };

    // 列定义
    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => a.name?.localeCompare(b.name),
        },
        {
            title: 'Color',
            dataIndex: 'color',
            key: 'color',
            width: 60,
            hideInTable: window.innerWidth < 760,
            render: (text) => {
                if (!text) return '-';
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div 
                            style={{ 
                                width: '24px', 
                                height: '24px', 
                                backgroundColor: text,
                                border: '1px solid #ddd',
                                borderRadius: '4px'
                            }}
                        />
                        {/* <span>{text}</span> */}
                    </div>
                );
            }
        },
        // {
        //     title: 'Parent Category',
        //     dataIndex: 'parentId',
        //     key: 'parentId',
        //     width: 200,
        //     render: (text) => {
        //         if (!text) return '-';
        //         const parent = flatData.find(item => item.id === text);
        //         return parent?.name || '-';
        //     }
        // },
        {
            title: 'Created Date',
            dataIndex: 'created_date',
            key: 'created_date',
            align: 'right', // 时间列右对齐
            render: (text) => {
                return text ? moment(text).format('YYYY-MM-DD HH:mm:ss') : '-';
            }
        },
        {
            title: 'Modified Date',
            dataIndex: 'modified_date',
            key: 'modified_date',
            align: 'right', // 时间列右对齐
            render: (text) => {
                return text ? moment(text).format('YYYY-MM-DD HH:mm:ss') : '-';
            }
        },
        {
            title: 'Action',
            key: 'action',
            width: 120,
            render: (_, record) => (
                <Space size="middle">
                    {canWrite && <a onClick={() => handleEditClick(record)}>
                        <i className="fas fa-edit text-lg"></i>
                    </a>}
                    <a onClick={(e) => {
                        e.stopPropagation();
                        navigateAndCloseTabs(`/admin/facility-category-detail/${record.id}`, '/admin/facility-category-detail/');
                    }}>
                        <i className="fas fa-expand-arrows-alt text-lg"></i>
                    </a>
                    {canFullAccess && <Popconfirm
                        title="Are you sure to delete this category?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <a style={{ color: '#ff4d4f' }}>
                            <i className="fas fa-trash-alt text-lg"></i>
                        </a>
                    </Popconfirm>}
                </Space>
            ),
        },
    ];

    return (
        <div className="m-5">
            <ConfigProvider locale={enUSIntl}>
                <ProTable
                    actionRef={actionRef}
                    columns={columns}
                    rowKey="id"
                    search={false}
                    options={{
                        reload: true, // 保留刷新按钮
                        setting: false, // 移除 settings 图标
                        density: false, // 移除 density 按钮
                    }}
                    request={async () => {
                        try {
                            const response = await api.filterFacilityCategory('');
                            
                            // 处理不同的响应格式
                            let flatDataSource = [];
                            if (Array.isArray(response.data)) {
                                flatDataSource = response.data;
                            } else if (response.data && Array.isArray(response.data.results)) {
                                flatDataSource = response.data.results;
                            } else if (response.data && Array.isArray(response.data.data)) {
                                flatDataSource = response.data.data;
                            }

                            // 保存平铺数据用于 parentId 选择器
                            setFlatData(flatDataSource);
                            
                            // 转换为树形结构
                            const treeData = convertToTreeData(flatDataSource);
                            
                            // 获取所有行的 key，设置为默认展开状态（包括没有子节点的行）
                            const allRowKeys = getAllRowKeys(treeData);
                            setExpandedRowKeys(allRowKeys);
                            
                            // 更新 Redux（保存平铺数据）
                            dispatch(actionsCategory.FacilityCategorySuccess(flatDataSource));

                            return {
                                data: treeData,
                                success: true,
                                total: treeData.length,
                            };
                        } catch (error) {
                            return {
                                data: [],
                                success: false,
                                total: 0,
                            };
                        }
                    }}
                    toolBarRender={() => [
                        canWrite && <Button
                            key="button"
                            type="primary"
                            onClick={handleCreateClick}
                            style={{ margin: '20px 0' }}
                        >
                            New Category
                        </Button>,
                    ]}
                    pagination={false}
                    expandable={{
                        expandedRowKeys: expandedRowKeys,
                        onExpandedRowsChange: (expandedKeys) => {
                            setExpandedRowKeys(expandedKeys);
                        },
                    }}
                    onRow={(record) => ({
                        onDoubleClick: () => {
                            navigateAndCloseTabs(`/admin/facility-category-detail/${record.id}`, '/admin/facility-category-detail/');
                        },
                    })}
                />

                <ModalForm
                    title={editingRecord ? 'Edit Category' : 'Create New Category'}
                    form={form}
                    visible={modalVisible}
                    onVisibleChange={(visible) => {
                        setModalVisible(visible);
                        if (!visible) {
                            handleModalClose();
                        }
                    }}
                    onFinish={editingRecord ? handleEdit : handleCreate}
                    width={500}
                >
                    <ProFormText
                        name="name"
                        label="Category Name"
                        placeholder="Enter category name"
                        rules={[{ required: true, message: 'Please enter category name' }]}
                    />
                    <ProFormSelect
                        name="parentId"
                        label="Parent Category"
                        placeholder="Select parent category (Optional)"
                        options={getAllCategoryOptions()}
                        allowClear
                    />
                    <ProFormText
                        name="color"
                        label="Color"
                        fieldProps={{
                            type: 'color',
                            style: { height: '40px', width: '100%' }
                        }}
                        initialValue="#000000"
                    />
                </ModalForm>
            </ConfigProvider>
        </div>
    );
};

export default Facility_Category;
